'use client';

import Image from 'next/image';
import { ForwardedRef, forwardRef, useLayoutEffect, useRef, useState } from 'react';

import {
  ChainLinkIcon,
  LandingArrowDown,
  LandingEllipse,
  TelegramLandingIcon,
  WhatsAppLandingIcon
} from 'components/icons';
import { useIsVisible } from 'hooks/useIsVisible';
import { LandingQuery } from 'generated/operations';

import MemberImg1 from 'app/(unauthenticated)/landing-page/assets/m1.png';
import MemberImg2 from 'app/(unauthenticated)/landing-page/assets/m2.png';
import MemberImg3 from 'app/(unauthenticated)/landing-page/assets/m3.png';
import MemberImg4 from 'app/(unauthenticated)/landing-page/assets/m4.png';
import { VideoModal } from './VideoModal';

const defaultMembers = [
  MemberImg1,
  MemberImg2,
  MemberImg3,
  MemberImg4
];

const getAppIcon = (connectedApp: string) => {
  switch (connectedApp.toLowerCase()) {
    case 'whatsapp':
      return <WhatsAppLandingIcon height={20} width={20} classNames={'h-10 w-10'} />;
    case 'telegram':
    default:
      return <TelegramLandingIcon height={20} width={20} classNames={'h-10 w-10'} />;
  }
};

export const HeroSection = forwardRef((props: {
  landingData?: LandingQuery['landing'],
  connectedApp: string
}, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    landingData,
    connectedApp
  } = props;

  const imgWrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const communityInfoRef = useRef(null);
  const videoRef = useRef(null);

  const isVisibleImg = useIsVisible(imgWrapperRef);
  const isVisibleCommunityInfo = useIsVisible(communityInfoRef);
  const isVisibleVideo = useIsVisible(videoRef);

  const [imgHeight, setHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (!imgRef.current || !landingData?.headerImage) return;
    const resizeObserver = new ResizeObserver(([entry]) => {
      setHeight(entry.contentBoxSize[0].blockSize);
    });
    resizeObserver.observe(imgRef.current);
    return () => resizeObserver.disconnect(); // clean up
  }, [landingData?.headerImage]);

  const members = landingData?.icons || defaultMembers;
  return (
    <div
      className="bg-[#626cfe] pt-10 px-10 sm-1/2:pt-5 sm-1/2:px-5 text-white font-poppins relative"
      style={{ marginBottom: `${imgHeight / 2}px`, paddingBottom: `${imgHeight / 2}px`}}
      ref={ref}
    >
      <div className="container">
        <div className="grid grid-cols-12 gap-4">
          <div className={`col-span-8 sm:col-span-12 duration-1000 transition-all ${isVisibleCommunityInfo ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'}`} ref={communityInfoRef}>
            <div className="flex flex-1 flex-col gap-4">
              <p className="font-semibold text-[70px] lg:text-5xl md:text-4xl leading-[120%]">
                <span>{landingData?.headerTitle}</span>
              </p>
              <p className="text-lg font-light">{landingData?.headerSubtitle}</p>
              <div className="flex mt-2 gap-4 md:flex-col">
                <div className="flex flex-1 gap-5">
                  <div>
                    {getAppIcon(connectedApp)}
                  </div>
                  <div className="flex">
                    <div className="flex">
                      {members.map((member, index) => (
                        <div className="relative min-w-[40px]" style={{ left: `-${index * 4}px` }} key={index}>
                          <Image src={member} width={21} height={21} alt={`member-${index + 1}`}
                                 className="!h-10 !w-10 aspect-square rounded-full" />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 sm-1/2:flex-col">
                      <span className="text-[22px]/[100%] font-semibold">{landingData?.membersNumber}</span>
                      <span className="text-[18px]/[100%] font-light">Members</span>
                    </div>
                  </div>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <ChainLinkIcon height={24} width={24} />
                  <div className="flex gap-1 flex-wrap">
                    {landingData?.headerTags?.split(' ').map((tag, index) => (
                      <span className="text-sm font-light" key={`${tag}-${index}`}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-start-11 col-span-2 lg:col-start-10 lg:col-span-3 sm:col-start-1 sm:col-span-12" ref={videoRef}>
            {landingData?.youtubeVideoUrl && (
              <div className={`flex flex-col sm:flex-row md:items-center gap-3 sm-1/2:gap-2 duration-1000 transition-all ${isVisibleVideo ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}`}>
                <div className="relative flex md:flex-none xs:max-w-[175px]">
                  <LandingEllipse classNames="!max-w-full !w-auto" />
                  <div className="-rotate-6 absolute top-0">
                    <LandingEllipse classNames="!max-w-full !w-auto" />
                  </div>
                  <p
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-base">Watch
                    video</p>
                </div>
                <div className="flex flex-1 justify-center sm:rotate-[255deg] md:flex-none">
                  <LandingArrowDown />
                </div>
                <div className="flex-1 md:flex-none">
                  <div className="md:w-full lg:w-3/4 w-full mx-auto">
                    <VideoModal videoUrl={landingData.youtubeVideoUrl} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div ref={imgWrapperRef} className="relative mt-5">
          <div className="grid grid-cols-12">
            <div className="col-span-12">
              {!!landingData?.headerImage && (
                <div className={`overflow-hidden border-[6px] border-white-alpha-40 rounded-2xl absolute transition-all duration-700 ${isVisibleImg ? 'opacity-100 translate-y-0 ' : 'opacity-0 translate-y-full'}`}>
                  <Image
                    src={landingData?.headerImage}
                    width={1100}
                    height={500}
                    ref={imgRef}
                    className={`md:object-fill max-h-[500px] md:max-h-full object-cover`}
                    alt=""
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

HeroSection.displayName = 'HeroSection';
